import { Box, FormControl, Icon, IconSize, Select, Text, TextFieldAppearance, TextFieldSize } from '@bechtel/gds';
import { ToolCatalog, Uom } from '@bechtel/ht-types';
import { Formik } from 'formik';
import { FC, useEffect, useState } from 'react';
import { number, object, string } from 'yup';
import { ToolOrderCatalogItem } from '~/views/reservations/types';
import { EditCountField } from '../molecules';
import { ToolCatalogSelector } from '../tool-catalog-selector';
import {
  FooterButtonStyled,
  FooterStyled,
  FormItemStyled,
  FormTextStyled,
  ModalHeaderStyled,
  NonCatalogItemStyled,
  TextAreaStyled,
  UomListStyled,
} from './styled';

import { get } from 'lodash';
import { useGetUomList } from '~/hooks';
export interface AddNonCatalogItemProps {
  onAddUpdateNonCatalogItem: (nonCatalogItem: ToolOrderCatalogItem) => void;
  nonCatalogItem: ToolOrderCatalogItem | undefined;
  onClose: () => void;
  isDispatchModule: boolean;
}

const validationSchema = object().shape({
  nonCatalogDescription: string()
    .trim()
    .min(1, `Please enter description`)
    .max(255, `Please limit description to 255 characters`)
    .required(`Please enter description`),
  quantity: number().min(1, 'Please enter quantity').required(`quantity is mandatory`),
  nonCatalogUomId: number().min(1, 'Please enter quantity Uom').required(`Uom is mandatory`),
  nonCatalogReferenceUrl: string().max(1024, `Please limit Url to 1024 characters`).url('Enter correct Url.'),
});
const UomItem = ({ item }: { item: Uom }) => {
  return (
    <Text color="grey.500" size="300" style={{ height: '22px' }}>
      {item?.description + ' (' + item?.abbreviation + ')'}
    </Text>
  );
};
const ErrorField = ({ error }) => {
  return (
    <Box pt={{ sm: '200' }} pl={{ sm: '300' }}>
      <Text size="250" color="bechtel.red">
        {error}
      </Text>
    </Box>
  );
};
export const AddUpdateNonCatalogItem: FC<AddNonCatalogItemProps> = ({
  onAddUpdateNonCatalogItem,
  onClose,
  nonCatalogItem,
  isDispatchModule = false,
}) => {
  const { getUomList, data: uomListData } = useGetUomList();
  const [selectedUom, setSelectedUom] = useState<Uom>();
  const uomList = get(uomListData, 'searchUoms.items', []);
  const selectedItem =
    uomList?.find((item: Uom) => {
      return item?.id == nonCatalogItem?.nonCatalogUomId;
    }) || uomList[0];

  useEffect(() => {
    getUomList();
  }, []);

  useEffect(() => {
    setSelectedUom(selectedItem);
  }, [uomList]);

  return (
    <NonCatalogItemStyled>
      <ModalHeaderStyled isDispatchModule={isDispatchModule}>
        <Box pl={{ sm: '300' }}>
          <Text size="400" weight="600" color={isDispatchModule ? 'white' : 'grey.700'}>
            Request a non-catalog item
          </Text>
        </Box>
        <Box px={{ sm: '200' }} style={{ cursor: 'pointer' }} onClick={onClose}>
          <Icon
            iconStyle="regular"
            icon="xmark"
            size={IconSize.LARGE}
            fillColor={isDispatchModule ? 'white' : 'grey.600'}
          ></Icon>
        </Box>
      </ModalHeaderStyled>
      <Formik
        initialValues={{
          id: nonCatalogItem?.id || '',
          nonCatalogDescription: nonCatalogItem?.nonCatalogDescription || '',
          nonCatalogReferenceUrl: nonCatalogItem?.nonCatalogReferenceUrl || '',
          similarToolCatalog: nonCatalogItem?.similarToolCatalog || null,
          nonCatalogUomId: nonCatalogItem?.nonCatalogUomId || selectedItem?.id || null,
          quantity: nonCatalogItem?.quantity || 0,
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          onAddUpdateNonCatalogItem({
            ...values,
            quantity: values.quantity,
            id: nonCatalogItem?.id || '',
            similarToolCatalog: values?.similarToolCatalog,
          });
          onClose();
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, dirty, isValid, touched, setFieldValue, errors }) => {
          return (
            <>
              <Box flexDirection={{ sm: 'column' }} style={{ padding: '8px' }}>
                <form style={{ width: '100%' }}>
                  <FormItemStyled>
                    <FormControl label={'Description'}>
                      <TextAreaStyled
                        name="nonCatalogDescription"
                        onChange={handleChange}
                        value={values.nonCatalogDescription}
                        rows={2}
                        changed={dirty}
                      />
                    </FormControl>
                    {errors.nonCatalogDescription ? <ErrorField error={errors.nonCatalogDescription} /> : null}

                    <FormTextStyled>
                      Provide a brief description of an item you’re requesting. Include any Make, Model number, and size
                      information.
                    </FormTextStyled>
                  </FormItemStyled>
                  <FormItemStyled>
                    <FormControl label={'Reference Link/Url'}>
                      <TextAreaStyled
                        name="nonCatalogReferenceUrl"
                        onChange={handleChange}
                        value={values.nonCatalogReferenceUrl || ''}
                        rows={2}
                        changed={dirty}
                        placeholder="https://www.amazon.com/productURL or https://www.walmart.com/productURL"
                      />
                    </FormControl>
                    {errors.nonCatalogReferenceUrl ? <ErrorField error={errors.nonCatalogReferenceUrl} /> : null}
                    <FormTextStyled>Provide a link or URL to the item requesting.</FormTextStyled>
                  </FormItemStyled>
                  <FormItemStyled>
                    <ToolCatalogSelector
                      formLabel="Similar product"
                      selectedItem={values.similarToolCatalog}
                      onSelectingToolCatalog={(toolCatalog: ToolCatalog) => {
                        setFieldValue('similarToolCatalog', toolCatalog);
                      }}
                    ></ToolCatalogSelector>
                    <FormTextStyled>
                      Select a similar item if it is similar to another product in the catalog.
                    </FormTextStyled>
                  </FormItemStyled>
                  <Box alignItems={{ sm: 'baseline' }} mt={{ sm: '400' }} pl={{ sm: '300' }}>
                    <FormTextStyled>Quantity : </FormTextStyled>
                    <Box flexDirection={{ sm: 'column' }}>
                      <EditCountField
                        count={values.quantity}
                        onIncrement={() => {
                          setFieldValue('quantity', values.quantity + 1);
                        }}
                        onDecrement={() => {
                          if (values.quantity > 0) {
                            setFieldValue('quantity', values.quantity - 1);
                          }
                        }}
                        onCountTextChange={(count) => {
                          setFieldValue('quantity', count);
                        }}
                      ></EditCountField>
                      {errors.quantity ? <ErrorField error={errors.quantity} /> : null}
                    </Box>
                    <UomListStyled>
                      <Select
                        loading={uomList && uomList.length < 1}
                        size={TextFieldSize.SMALL}
                        name="nonCatalogUomId"
                        appearance={TextFieldAppearance.TRANSPARENT}
                        items={uomList}
                        itemComponent={UomItem}
                        selectedComponent={UomItem}
                        selectedItem={selectedUom}
                        onSelectedItemChange={({ selectedItem }) => {
                          setFieldValue('nonCatalogUomId', Number(selectedItem.id));
                          setSelectedUom(selectedItem);
                        }}
                      />
                    </UomListStyled>
                  </Box>
                </form>
              </Box>
              <FooterStyled>
                <Box p={{ sm: '500' }}>
                  <FooterButtonStyled onClick={() => handleSubmit()} disabled={!dirty || !isValid || !touched}>
                    <Box px={{ sm: '500' }}>
                      {!nonCatalogItem && <Text>Add to cart</Text>}
                      {nonCatalogItem && <Text>Update to cart</Text>}
                    </Box>
                  </FooterButtonStyled>
                </Box>
              </FooterStyled>
            </>
          );
        }}
      </Formik>
    </NonCatalogItemStyled>
  );
};
